// Búsqueda rápida de persona (padrón). Reutilizable en Central V3 (global) y Radar institucional.
// No reemplaza la cola: aparece cuando el operador la necesita (p.ej. llamada de un familiar).
import { useState, useRef, useEffect } from "react";
import { Search, X, Activity } from "lucide-react";

export default function PersonaQuickSearch({ source, onOpen, resetKey, placeholder = "Buscar persona (Juana, Pérez…)" }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [open, setOpen] = useState(false);
  const box = useRef(null);

  // DEMO-004: al cambiar de escenario/dataset la búsqueda anterior ya no aplica. Se limpia el
  // texto, los resultados y el overlay (antes quedaba "Sin coincidencias" de datos reales encima
  // del escenario nuevo). Sólo se limpia estado incompatible: nada global.
  useEffect(() => { setQ(""); setRes([]); setOpen(false); }, [resetKey]);

  useEffect(() => {
    let vivo = true;
    if (!q.trim()) { setRes([]); return; }
    const t = setTimeout(async () => {
      try { const r = await source(q.trim()); if (vivo) { setRes(r); setOpen(true); } } catch { if (vivo) setRes([]); }
    }, 180);
    return () => { vivo = false; clearTimeout(t); };
  }, [q, source]);

  useEffect(() => {
    const h = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div ref={box} className="relative">
      <div data-testid="persona-quick-search-box" className="flex items-center gap-2 rounded-lg px-2.5 py-1.5">
        <Search className="h-4 w-4 text-slate-400" />
        <input data-testid="persona-quick-search" aria-label={placeholder} value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => q && setOpen(true)}
          placeholder={placeholder} className="w-52 bg-transparent text-sm text-slate-700 outline-none dark:text-slate-200" />
        {q && <button type="button" onClick={() => { setQ(""); setRes([]); }} aria-label="Limpiar la búsqueda de persona"
          className="rounded text-slate-400 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#01CBE3]"
          data-testid="persona-quick-search-clear"><X className="h-3.5 w-3.5" /></button>}
      </div>
      {open && q.trim() && (
        <div data-testid="persona-search-results" className="absolute right-0 z-50 mt-1 max-h-80 w-80 overflow-y-auto rounded-xl border border-slate-200 bg-white p-1 shadow-xl dark:border-[#263B5D] dark:bg-[#0B111E]">
          {res.length === 0 ? (
            <p className="px-3 py-3 text-sm text-slate-400">Sin coincidencias.</p>
          ) : res.map((r) => (
            <button key={r.id} data-testid={`persona-search-item-${r.id}`} onClick={() => { onOpen(r); setOpen(false); }}
              className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-[#192C47]">
              <span className="min-w-0">
                <span className="block truncate text-sm font-semibold text-slate-800 dark:text-white">{r.nombre}{r.edad ? <span className="font-normal text-slate-400"> · {r.edad}</span> : null}</span>
                <span className="block truncate text-xs text-slate-400">{r.sitio_nombre || r.ciudad || ""}</span>
              </span>
              {r.caso ? <span className="inline-flex shrink-0 items-center gap-1 rounded bg-[#FE6A55]/15 px-1.5 py-0.5 text-[10px] font-bold text-[#C2410C]"><Activity className="h-3 w-3" /> Caso activo</span>
                      : <span className="shrink-0 text-[10px] text-slate-400">Sin caso</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
